import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import Toast from "../components/Toast";
import { stockService, profileService } from "../services/apiService";

const TABS = [
  { id: "clients",   label: "Clients",    icon: "fa-building" },
  { id: "users",     label: "Users",      icon: "fa-users" },
  { id: "fieldConfig", label: "Field Config", icon: "fa-sliders-h" },
  { id: "dashboard", label: "Dashboard",  icon: "fa-th-large" },
  { id: "menu",      label: "Menu",       icon: "fa-bars" },
  { id: "settings",  label: "Settings",   icon: "fa-cog" },
];

// ─── Menu Tab (per-client feature toggles, e.g. ChatBot) ───────────────────────
// Feature flags surfaced as menu items. Each is a boolean dashboard setting.
const MENU_FEATURES = [
  { settingKey: "chatbotEnabled",       label: "ChatBot",             icon: "fa-robot",                color: "text-indigo-500" },
  { settingKey: "gstReportEnabled",     label: "GST Report",          icon: "fa-file-invoice-dollar",  color: "text-teal-600"   },
  { settingKey: "whatsappBillEnabled",  label: "WhatsApp Bill Share", icon: "fa-whatsapp",             color: "text-green-600"  },
];

const MenuTab = ({ showToast }) => {
  const [clients, setClients]   = useState([]);
  const [selected, setSelected] = useState("");
  const [settings, setSettings] = useState([]);
  const [loading, setLoading]   = useState(false);

  useEffect(() => { stockService.adminGetClients().then(setClients).catch(() => {}); }, []);

  const loadSettings = async (clientCode) => {
    setSelected(clientCode);
    if (!clientCode) { setSettings([]); return; }
    setLoading(true);
    try { setSettings(await stockService.adminGetDashboardSettings(clientCode)); }
    catch { setSettings([]); showToast("error", "Failed to load menu settings"); }
    finally { setLoading(false); }
  };

  const handleToggle = async (item) => {
    const newVal = !(item.settingValue === "true");
    try {
      await stockService.adminSaveDashboardSetting(selected, {
        settingKey: item.settingKey,
        settingValue: String(newVal),
        label: item.label,
      });
      showToast("success", `${item.label} ${newVal ? "enabled" : "disabled"}`);
      // Notify sidebar to re-check feature flags immediately
      if (item.settingKey === "gstReportEnabled") {
        window.dispatchEvent(new Event("gst-report-changed"));
      }
      loadSettings(selected);
    } catch { showToast("error", "Save failed"); }
  };

  const handleChannelChange = async (settingKey, value, label) => {
    try {
      await stockService.adminSaveDashboardSetting(selected, {
        settingKey, settingValue: value, label,
      });
      showToast("success", `${label} set to ${value}`);
      loadSettings(selected);
      // Notify the sidebar to re-check the offer channel immediately (applies
      // when the admin is changing their own client's setting).
      window.dispatchEvent(new Event("offer-channel-changed"));
    } catch { showToast("error", "Save failed"); }
  };

  const merged = MENU_FEATURES.map((p) => ({
    ...p, ...(settings.find((s) => s.settingKey === p.settingKey) || {}),
    saved: !!settings.find((s) => s.settingKey === p.settingKey),
  }));

  const offerChannelValue =
    settings.find((s) => s.settingKey === "offerChannel")?.settingValue || "off";

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-3">
      <div className="flex items-center gap-3 flex-shrink-0">
        <select value={selected} onChange={(e) => loadSettings(e.target.value)}
          className="px-3 py-1.5 border border-blue-300 rounded-lg text-sm">
          <option value="">Select Client</option>
          {clients.map((c) => <option key={c.clientId} value={c.clientCode}>{c.clientName} ({c.clientCode})</option>)}
        </select>
      </div>

      {selected && (
        <div className="overflow-auto flex-1 border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700 text-xs uppercase sticky top-0">
              <tr>
                <th className="py-2 px-4 text-left">Menu</th>
                <th className="py-2 px-4 text-center">Visible to Client</th>
                <th className="py-2 px-4 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3} className="py-8 text-center"><i className="fas fa-spinner fa-spin text-blue-400" /></td></tr>
              ) : (
                <>
                  {merged.map((item, idx) => {
                    const on = item.settingValue === "true";
                    return (
                      <tr key={item.settingKey} className={`border-t ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                        <td className="py-2 px-4">
                          <div className="flex items-center gap-2">
                            <i className={`fas ${item.icon} ${item.color} text-sm`} />
                            <div>
                              <p className="font-medium text-xs">{item.label}</p>
                              <p className="text-xs text-gray-400 font-mono">{item.settingKey}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-2 px-4 text-center">
                          <button
                            onClick={() => handleToggle(item)}
                            aria-label={`Toggle ${item.label}`}
                            className={`w-10 h-5 rounded-full transition ${on ? "bg-green-500" : "bg-gray-300"}`}
                          >
                            <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
                          </button>
                        </td>
                        <td className="py-2 px-4 text-center">
                          {on
                            ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700"><i className="fas fa-check-circle mr-1" />On</span>
                            : <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500"><i className="fas fa-power-off mr-1" />Off</span>}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Offers — channel dropdown parallel to the menu label */}
                  <tr className="border-t bg-white">
                    <td className="py-2 px-4">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-bullhorn text-indigo-500 text-sm" />
                        <div>
                          <p className="font-medium text-xs">Offers</p>
                          <p className="text-xs text-gray-400 font-mono">offerChannel</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-4 text-center">
                      <select
                        value={offerChannelValue}
                        onChange={(e) => handleChannelChange("offerChannel", e.target.value, "Offers")}
                        className="px-2 py-1 border border-blue-300 rounded-lg text-xs"
                      >
                        <option value="off">Off</option>
                        <option value="wa_me">WhatsApp Link (free)</option>
                        <option value="sms">SMS</option>
                        <option value="whatsapp_api">WhatsApp API</option>
                      </select>
                    </td>
                    <td className="py-2 px-4 text-center">
                      {offerChannelValue !== "off"
                        ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700"><i className="fas fa-check-circle mr-1" />On</span>
                        : <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500"><i className="fas fa-power-off mr-1" />Off</span>}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Clients Tab ──────────────────────────────────────────────────────────────
const ClientsTab = ({ showToast }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editClient, setEditClient] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { loadClients(); }, []);

  const loadClients = async () => {
    setLoading(true);
    try { setClients(await stockService.adminGetClients()); }
    catch { setClients([]); showToast("error", "Failed to load clients"); }
    finally { setLoading(false); }
  };

  const handleEdit = (client) => {
    setOpenDropdown(null);
    setEditClient(client);
    setEditForm({
      billingPlan: client.billingPlan || "",
      billingExpiryDate: client.billingExpiryDate || "",
      billingDaysAdd: "",
      paymentStatus: client.paymentStatus || "",
      isActive: client.isActive !== false,
    });
  };

  const handleSave = async () => {
    if (!editClient) return;
    setSaving(true);
    try {
      const updates = {};
      if (editForm.billingPlan) updates.billingPlan = editForm.billingPlan;
      if (editForm.billingExpiryDate) updates.billingExpiryDate = editForm.billingExpiryDate;
      if (editForm.billingDaysAdd && parseInt(editForm.billingDaysAdd)) updates.billingDaysAdd = parseInt(editForm.billingDaysAdd);
      if (editForm.paymentStatus) updates.paymentStatus = editForm.paymentStatus;
      updates.isActive = editForm.isActive;
      await stockService.adminUpdateClient(editClient.clientId, updates);
      showToast("success", "Client updated");
      setEditClient(null);
      loadClients();
    } catch { showToast("error", "Update failed"); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await stockService.adminDeleteClient(deleteConfirm.clientId);
      showToast("success", `Client "${deleteConfirm.clientName}" deleted`);
      setDeleteConfirm(null);
      loadClients();
    } catch { showToast("error", "Delete failed"); }
  };

  const getDaysLeft = (expiry) => {
    if (!expiry) return null;
    return Math.ceil((new Date(expiry) - new Date()) / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="overflow-auto flex-1">
        <table className="min-w-full text-sm">
          <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 text-xs uppercase sticky top-0 z-10">
            <tr>
              <th className="py-2 px-3 text-left">ID</th>
              <th className="py-2 px-3 text-left">Client</th>
              <th className="py-2 px-3 text-left">Code</th>
              <th className="py-2 px-3 text-left">Plan</th>
              <th className="py-2 px-3 text-center">Days Left</th>
              <th className="py-2 px-3 text-center">Payment</th>
              <th className="py-2 px-3 text-center">Status</th>
              <th className="py-2 px-3 text-center">T&C</th>
              <th className="py-2 px-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="py-10 text-center"><i className="fas fa-spinner fa-spin text-blue-400 text-2xl" /></td></tr>
            ) : clients.map((c, idx) => {
              const days = getDaysLeft(c.billingExpiryDate);
              const daysCls = days === null ? "text-gray-400" : days < 0 ? "text-white bg-black" : days <= 7 ? "text-red-700 bg-red-100" : days <= 15 ? "text-yellow-700 bg-yellow-100" : "text-green-700 bg-green-100";
              return (
                <tr key={c.clientId} className={`border-t hover:bg-indigo-50 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                  <td className="py-2 px-3 text-xs text-gray-500">{c.clientId}</td>
                  <td className="py-2 px-3 font-medium">{c.clientName || "-"}</td>
                  <td className="py-2 px-3 text-xs font-mono">{c.clientCode || "-"}</td>
                  <td className="py-2 px-3 text-xs">{c.billingPlan || "-"}</td>
                  <td className="py-2 px-3 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${daysCls}`}>{days === null ? "-" : days < 0 ? "Expired" : `${days}d`}</span></td>
                  <td className="py-2 px-3 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.paymentStatus === "PAID" ? "bg-green-100 text-green-700" : c.paymentStatus === "PENDING" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-500"}`}>{c.paymentStatus || "-"}</span></td>
                  <td className="py-2 px-3 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.isActive !== false ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{c.isActive !== false ? "Active" : "Inactive"}</span></td>
                  <td className="py-2 px-3 text-center">{c.tcAccepted ? <i className="fas fa-check-circle text-green-500" /> : <i className="fas fa-times-circle text-red-400" />}</td>
                  <td className="py-2 px-3 text-center">
                    <div className="relative inline-block">
                      <button
                        onClick={() => setOpenDropdown(openDropdown === c.clientId ? null : c.clientId)}
                        className="text-gray-500 hover:bg-gray-100 px-2 py-1 rounded text-xs"
                      >
                        <i className="fas fa-ellipsis-v" />
                      </button>
                      {openDropdown === c.clientId && (
                        <div className="absolute right-0 mt-1 w-28 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                          <button
                            onClick={() => handleEdit(c)}
                            className="flex items-center gap-2 w-full px-3 py-2 text-xs text-gray-700 hover:bg-indigo-50"
                          >
                            <i className="fas fa-edit text-indigo-500" />Edit
                          </button>
                          <button
                            onClick={() => { setOpenDropdown(null); setDeleteConfirm(c); }}
                            className="flex items-center gap-2 w-full px-3 py-2 text-xs text-red-600 hover:bg-red-50"
                          >
                            <i className="fas fa-trash text-red-500" />Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {editClient && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4" onClick={() => setEditClient(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 bg-gradient-to-r from-indigo-100 to-blue-100 border-b rounded-t-xl flex justify-between items-center">
              <h3 className="font-bold text-blue-800"><i className="fas fa-building mr-2" />{editClient.clientName}</h3>
              <button onClick={() => setEditClient(null)} className="text-gray-400 hover:text-gray-600"><i className="fas fa-times" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Plan</label>
                  <select value={editForm.billingPlan} onChange={(e) => setEditForm((p) => ({ ...p, billingPlan: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">Select</option>
                    <option value="FREE">FREE</option>
                    <option value="BASIC">BASIC</option>
                    <option value="PRO">PRO</option>
                    <option value="ENTERPRISE">ENTERPRISE</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Payment Status</label>
                  <select value={editForm.paymentStatus} onChange={(e) => setEditForm((p) => ({ ...p, paymentStatus: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">Select</option>
                    <option value="PAID">PAID</option>
                    <option value="PENDING">PENDING</option>
                    <option value="OVERDUE">OVERDUE</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Expiry Date</label>
                <input type="date" value={editForm.billingExpiryDate} onChange={(e) => setEditForm((p) => ({ ...p, billingExpiryDate: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Add/Remove Days</label>
                <input type="number" value={editForm.billingDaysAdd} onChange={(e) => setEditForm((p) => ({ ...p, billingDaysAdd: e.target.value }))} placeholder="e.g. 30 or -10" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div className="flex items-center justify-between pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={editForm.isActive} onChange={(e) => setEditForm((p) => ({ ...p, isActive: e.target.checked }))} className="w-4 h-4 accent-green-600" />
                  <span className="text-sm font-medium text-gray-700">Client Active</span>
                </label>
              </div>
              <button onClick={handleSave} disabled={saving} className={`w-full py-2 rounded-lg font-semibold text-sm text-white transition ${saving ? "bg-gray-400" : "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"}`}>
                {saving ? <><i className="fas fa-spinner fa-spin mr-1" />Saving...</> : <><i className="fas fa-save mr-1" />Save</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Popup */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-80 p-5">
            <div className="flex items-center gap-2 mb-3">
              <i className="fas fa-trash text-red-500 text-lg" />
              <h3 className="font-bold text-gray-800">Delete Client?</h3>
            </div>
            <p className="text-sm text-gray-600 mb-1">You are about to permanently delete:</p>
            <p className="text-sm font-semibold text-gray-800 mb-1">{deleteConfirm.clientName}</p>
            <p className="text-xs text-gray-500 mb-4">Code: {deleteConfirm.clientCode}</p>
            <p className="text-xs text-red-500 mb-4">This action cannot be undone.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={confirmDelete} className="px-4 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-semibold">Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Role Multi-Select ───────────────────────────────────────────────────────
const RoleMultiSelect = ({ value, onChange, roles }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selected = value ? value.split(",").map((r) => r.trim()).filter(Boolean) : [];

  const toggle = (role) => {
    const next = selected.includes(role)
      ? selected.filter((r) => r !== role)
      : [...selected, role];
    onChange(next.join(","));
  };

  const filtered = roles.filter((r) =>
    r.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="relative" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}>
      <div
        tabIndex={0}
        onClick={() => setOpen((p) => !p)}
        className="min-w-[140px] min-h-[26px] px-1.5 py-0.5 border rounded text-xs cursor-pointer bg-white flex flex-wrap gap-1 items-center"
      >
        {selected.length === 0
          ? <span className="text-gray-400">-- Select --</span>
          : selected.map((r) => (
              <span key={r} className="flex items-center gap-0.5 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full text-xs">
                {r}
                <button type="button" onMouseDown={(e) => { e.stopPropagation(); toggle(r); }} className="ml-0.5 text-blue-400 hover:text-red-500">&times;</button>
              </span>
            ))}
        <i className="fas fa-chevron-down text-gray-400 ml-auto text-[10px]" />
      </div>
      {open && (
        <div className="absolute left-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-30">
          <div className="p-1.5 border-b">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
          <div className="max-h-40 overflow-y-auto">
            {filtered.length === 0
              ? <p className="text-xs text-gray-400 px-3 py-2">No roles found</p>
              : filtered.map((r) => (
                  <button
                    key={r} type="button"
                    onMouseDown={(e) => { e.preventDefault(); toggle(r); }}
                    className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs text-left hover:bg-indigo-50 ${
                      selected.includes(r) ? "bg-indigo-50 font-semibold text-indigo-700" : "text-gray-700"
                    }`}
                  >
                    <i className={`fas fa-${selected.includes(r) ? "check-square text-indigo-500" : "square text-gray-300"}`} />
                    {r}
                  </button>
                ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Users Tab ────────────────────────────────────────────────────────────────
const UsersTab = ({ showToast }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [roles, setRoles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [pwdConfirm, setPwdConfirm] = useState(null);
  const [mobileConfirm, setMobileConfirm] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadUsers();
    profileService.adminGetRoles().then(setRoles).catch(() => {});
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await profileService.adminGetAllUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch { setUsers([]); showToast("error", "Failed to load users"); }
    finally { setLoading(false); }
  };

  const startEdit = (u) => {
    setOpenDropdown(null);
    setEditId(u.perDId);
    setEditData({ name: u.name || "", email: u.email || "", status: u.status || "ACTIVE",
      role: u.role || "", newPassword: "", isPasswordChange: false,
      mobile: u.mobile || "", isMobileChange: false });
  };

  const handlePasswordBlur = () => {
    if (editData.newPassword && !editData.isPasswordChange) setPwdConfirm(editId);
  };
  const handlePasswordInput = (val) => {
    setEditData((p) => ({ ...p, newPassword: val, isPasswordChange: false }));
  };
  const confirmPasswordChange = (yes) => {
    if (!yes) setEditData((p) => ({ ...p, newPassword: "", isPasswordChange: false }));
    else setEditData((p) => ({ ...p, isPasswordChange: true }));
    setPwdConfirm(null);
  };

  const handleMobileBlur = (originalMobile) => {
    if (editData.mobile && editData.mobile !== originalMobile && !editData.isMobileChange)
      setMobileConfirm(editId);
  };
  const handleMobileInput = (val) => {
    setEditData((p) => ({ ...p, mobile: val, isMobileChange: false }));
  };
  const confirmMobileChange = (yes) => {
    if (!yes) setEditData((p) => ({ ...p, mobile: p.mobile, isMobileChange: false }));
    else setEditData((p) => ({ ...p, isMobileChange: true }));
    setMobileConfirm(null);
  };

  const saveEdit = async (user) => {
    if (editData.newPassword && !editData.isPasswordChange) { setPwdConfirm(editId); return; }
    if (editData.mobile && editData.mobile !== user.mobile && !editData.isMobileChange) { setMobileConfirm(editId); return; }
    setSaving(true);
    try {
      const payload = {
        name: editData.name,
        email: editData.email,
        status: editData.status,
        role: editData.role,
        isPasswordChange: String(editData.isPasswordChange),
        isMobileChange: String(editData.isMobileChange),
      };
      if (editData.isPasswordChange && editData.newPassword) payload.newPassword = editData.newPassword;
      if (editData.isMobileChange && editData.mobile) payload.newMobile = editData.mobile;
      await profileService.adminUpdateUser(user.perDId, payload);
      showToast("success", "User updated");
      setEditId(null);
      loadUsers();
    } catch { showToast("error", "Update failed"); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await profileService.adminDeleteUser(deleteConfirm.perDId);
      showToast("success", `User "${deleteConfirm.name}" deleted`);
      setDeleteConfirm(null);
      loadUsers();
    } catch { showToast("error", "Delete failed"); }
  };

  const filtered = users.filter((u) =>
    !filter.trim() || (u.clientCode || "").toLowerCase().includes(filter.trim().toLowerCase())
  );

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center gap-2 mb-3 flex-shrink-0">
        <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)}
          placeholder="Search by client code..."
          className="w-52 px-3 py-1.5 border border-blue-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none" />
        <span className="text-xs text-gray-500">{filtered.length} users</span>
      </div>

      <div className="overflow-auto flex-1 border border-gray-200 rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 text-gray-700 text-xs uppercase sticky top-0">
            <tr>
              <th className="py-2 px-3 text-left">Client Code</th>
              <th className="py-2 px-3 text-left">Name</th>
              <th className="py-2 px-3 text-left">Mobile</th>
              <th className="py-2 px-3 text-left">Email</th>
              <th className="py-2 px-3 text-center">Role</th>
              <th className="py-2 px-3 text-center">Password</th>
              <th className="py-2 px-3 text-center">Logged In</th>
              <th className="py-2 px-3 text-center">Last Logged In</th>
              <th className="py-2 px-3 text-center">Status</th>
              <th className="py-2 px-3 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="py-10 text-center"><i className="fas fa-spinner fa-spin text-blue-400 text-xl" /></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={10} className="py-10 text-center text-gray-400">No users found</td></tr>
            ) : filtered.map((u, idx) => (
              <tr key={u.perDId || idx} className={`border-t ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-indigo-50`}>
                <td className="py-2 px-3 text-xs font-semibold text-indigo-600">{u.clientCode || "-"}</td>

                <td className="py-2 px-3">
                  {editId === u.perDId
                    ? <input value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} className="w-full px-1 py-0.5 border rounded text-xs" />
                    : (u.name || "-")}
                </td>

                <td className="py-2 px-3 text-xs">
                  {editId === u.perDId
                    ? <input
                        value={editData.mobile}
                        onChange={(e) => handleMobileInput(e.target.value)}
                        onBlur={() => handleMobileBlur(u.mobile)}
                        className="w-28 px-1 py-0.5 border rounded text-xs"
                      />
                    : (u.mobile || "-")}
                </td>

                <td className="py-2 px-3">
                  {editId === u.perDId
                    ? <input value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} className="w-full px-1 py-0.5 border rounded text-xs" />
                    : <span className="text-xs text-gray-500">{u.email || "-"}</span>}
                </td>

                <td className="py-2 px-3 text-center">
                  {editId === u.perDId
                    ? <RoleMultiSelect value={editData.role} onChange={(val) => setEditData({ ...editData, role: val })} roles={roles} />
                    : <div className="flex flex-wrap gap-1 justify-center">
                        {(u.role || "").split(",").filter(Boolean).map((r) => (
                          <span key={r} className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">{r.trim()}</span>
                        ))}
                      </div>}
                </td>

                <td className="py-2 px-3 text-center">
                  {editId === u.perDId ? (
                    <input
                      type="password"
                      value={editData.newPassword}
                      onChange={(e) => handlePasswordInput(e.target.value)}
                      onBlur={handlePasswordBlur}
                      placeholder="New password"
                      className="w-28 px-1 py-0.5 border rounded text-xs"
                    />
                  ) : (
                    <span className="text-xs font-mono tracking-widest text-gray-400">
                      {u.hasPassword ? "••••••••" : "-"}
                    </span>
                  )}
                </td>

                <td className="py-2 px-3 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    u.loggedIn === "Y" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                  }`}>{u.loggedIn === "Y" ? "Y" : "N"}</span>
                </td>

                <td className="py-2 px-3 text-center text-xs text-gray-500 whitespace-nowrap">
                  {u.lastLoggedIn ? new Date(u.lastLoggedIn).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"}
                </td>

                <td className="py-2 px-3 text-center">
                  {editId === u.perDId
                    ? <select value={editData.status} onChange={(e) => setEditData({ ...editData, status: e.target.value })} className="px-1 py-0.5 border rounded text-xs">
                        <option value="ACTIVE">ACTIVE</option>
                        <option value="INACTIVE">INACTIVE</option>
                      </select>
                    : <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        u.status === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                      }`}>{u.status || "-"}</span>}
                </td>

                <td className="py-2 px-3 text-center">
                  {editId === u.perDId ? (
                    <div className="flex justify-center gap-1">
                      <button onClick={() => saveEdit(u)} disabled={saving} className="text-green-600 hover:bg-green-50 px-1.5 py-0.5 rounded disabled:opacity-50" title="Save">
                        {saving ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}
                      </button>
                      <button onClick={() => setEditId(null)} disabled={saving} className="text-gray-500 hover:bg-gray-100 px-1.5 py-0.5 rounded disabled:opacity-50" title="Cancel"><i className="fas fa-times" /></button>
                    </div>
                  ) : (
                    <div className="relative inline-block">
                      <button
                        onClick={() => setOpenDropdown(openDropdown === u.perDId ? null : u.perDId)}
                        className="text-gray-500 hover:bg-gray-100 px-2 py-1 rounded text-xs"
                      >
                        <i className="fas fa-ellipsis-v" />
                      </button>
                      {openDropdown === u.perDId && (
                        <div className="absolute right-0 mt-1 w-28 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                          <button
                            onClick={() => startEdit(u)}
                            className="flex items-center gap-2 w-full px-3 py-2 text-xs text-gray-700 hover:bg-indigo-50"
                          >
                            <i className="fas fa-edit text-indigo-500" />Edit
                          </button>
                          <button
                            onClick={() => { setOpenDropdown(null); setDeleteConfirm(u); }}
                            className="flex items-center gap-2 w-full px-3 py-2 text-xs text-red-600 hover:bg-red-50"
                          >
                            <i className="fas fa-trash text-red-500" />Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Change Confirm Popup */}
      {mobileConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-80 p-5">
            <div className="flex items-center gap-2 mb-3">
              <i className="fas fa-mobile-alt text-blue-500 text-lg" />
              <h3 className="font-bold text-gray-800">Change Mobile Number?</h3>
            </div>
            <div className="flex items-center gap-3 mb-3 text-sm">
              <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <p className="text-xs text-gray-400 mb-0.5">Old</p>
                <p className="font-semibold text-gray-700">{users.find(u => u.perDId === mobileConfirm)?.mobile || "-"}</p>
              </div>
              <i className="fas fa-arrow-right text-gray-400" />
              <div className="flex-1 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <p className="text-xs text-blue-400 mb-0.5">New</p>
                <p className="font-semibold text-blue-700">{editData.mobile}</p>
              </div>
            </div>
            <p className="text-xs text-yellow-600 mb-4">This will update the login username for this user.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => confirmMobileChange(false)} className="px-4 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50">No</button>
              <button onClick={() => confirmMobileChange(true)} className="px-4 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold">Yes, Change</button>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Confirm Popup */}
      {pwdConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-80 p-5">
            <div className="flex items-center gap-2 mb-3">
              <i className="fas fa-key text-yellow-500 text-lg" />
              <h3 className="font-bold text-gray-800">Change Password?</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">Are you sure you want to change the password for this user? This action will update their login credentials.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => confirmPasswordChange(false)} className="px-4 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50">No</button>
              <button onClick={() => confirmPasswordChange(true)} className="px-4 py-1.5 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-semibold">Yes, Change</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Popup */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-80 p-5">
            <div className="flex items-center gap-2 mb-3">
              <i className="fas fa-trash text-red-500 text-lg" />
              <h3 className="font-bold text-gray-800">Delete User?</h3>
            </div>
            <p className="text-sm text-gray-600 mb-1">You are about to permanently delete:</p>
            <p className="text-sm font-semibold text-gray-800 mb-4">{deleteConfirm.name} ({deleteConfirm.mobile})</p>
            <p className="text-xs text-red-500 mb-4">This action cannot be undone.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={confirmDelete} className="px-4 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-semibold">Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Field Config Tab ─────────────────────────────────────────────────────────
const FieldConfigTab = ({ showToast }) => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pageFilter, setPageFilter] = useState("");
  const [newField, setNewField] = useState({ fieldName: "", visible: true, mandatory: false, pageName: "" });

  useEffect(() => { stockService.adminGetClients().then(setClients).catch(() => {}); }, []);

  // Master list of all configurable fields (comma-separated pages per field)
  const ALL_FIELDS = [
    { fieldName: "productType", pageName: "stockCreate,product" },
    { fieldName: "manufacturer", pageName: "stockCreate,product" },
    { fieldName: "size", pageName: "stockCreate" },
    { fieldName: "stripCount", pageName: "stockCreate" },
    { fieldName: "expiryDate", pageName: "stockCreate" },
    { fieldName: "newBarcode", pageName: "stockCreate" },
    { fieldName: "boxNo", pageName: "stockCreate" },
    { fieldName: "lowStockThreshold", pageName: "settings" },
    { fieldName: "expiryAlertDays", pageName: "settings" },
  ];

  const loadConfig = async (clientCode) => {
    setSelectedClient(clientCode);
    if (!clientCode) { setConfigs([]); return; }
    setLoading(true);
    try {
      const saved = await stockService.adminGetFieldConfig(clientCode);
      // Merge: show all fields, with saved values if exist
      const merged = ALL_FIELDS.map((f) => {
        const existing = (saved || []).find((s) => s.fieldName === f.fieldName);
        return existing ? { ...f, ...existing, pageName: existing.pageName || f.pageName } : { ...f, visible: false, mandatory: false };
      });
      // Also include any saved fields not in master list
      (saved || []).forEach((s) => {
        if (!merged.find((m) => m.fieldName === s.fieldName)) {
          merged.push(s);
        }
      });
      setConfigs(merged);
    } catch { setConfigs([]); showToast("error", "Failed to load config"); }
    finally { setLoading(false); }
  };

  const toggleField = async (config, field, value) => {
    try {
      await stockService.adminSaveFieldConfig({ clientCode: selectedClient, fieldName: config.fieldName, [field]: value });
      setConfigs((prev) => prev.map((c) => c.fieldName === config.fieldName ? { ...c, [field]: value } : c));
    } catch { showToast("error", "Update failed"); }
  };

  const addField = async () => {
    if (!newField.fieldName.trim() || !selectedClient) return;
    setSaving(true);
    try {
      await stockService.adminSaveFieldConfig({ clientCode: selectedClient, ...newField });
      showToast("success", "Field added");
      setNewField({ fieldName: "", visible: true, mandatory: false, pageName: "" });
      loadConfig(selectedClient);
    } catch { showToast("error", "Add failed"); }
    finally { setSaving(false); }
  };

  const pages = [...new Set(configs.map((c) => c.pageName || "General"))];
  const filteredConfigs = pageFilter ? configs.filter((c) => (c.pageName || "General") === pageFilter) : configs;

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-3">
      <div className="flex items-center gap-3 flex-wrap flex-shrink-0">
        <select value={selectedClient} onChange={(e) => loadConfig(e.target.value)} className="px-3 py-1.5 border border-blue-300 rounded-lg text-sm">
          <option value="">Select Client</option>
          {clients.map((c) => <option key={c.clientId} value={c.clientCode}>{c.clientName} ({c.clientCode})</option>)}
        </select>
        {pages.length > 1 && (
          <select value={pageFilter} onChange={(e) => setPageFilter(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
            <option value="">All Pages</option>
            {pages.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        )}
      </div>

      {selectedClient && (
        <>
          <div className="flex items-end gap-2 flex-shrink-0 bg-gray-50 p-3 rounded-lg border border-gray-200 flex-wrap">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Field Name</label><input type="text" value={newField.fieldName} onChange={(e) => setNewField((p) => ({ ...p, fieldName: e.target.value }))} placeholder="e.g. expiryDate" className="w-36 px-3 py-1.5 border border-gray-300 rounded-lg text-xs" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Page</label><input type="text" value={newField.pageName} onChange={(e) => setNewField((p) => ({ ...p, pageName: e.target.value }))} placeholder="e.g. StockCreate" className="w-32 px-3 py-1.5 border border-gray-300 rounded-lg text-xs" /></div>
            <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={newField.visible} onChange={(e) => setNewField((p) => ({ ...p, visible: e.target.checked }))} />Visible</label>
            <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={newField.mandatory} onChange={(e) => setNewField((p) => ({ ...p, mandatory: e.target.checked }))} />Mandatory</label>
            <button onClick={addField} disabled={saving || !newField.fieldName.trim()} className="px-3 py-1.5 rounded-lg bg-green-500 hover:bg-green-600 text-white text-xs font-semibold disabled:opacity-50"><i className="fas fa-plus mr-1" />Add</button>
          </div>

          <div className="overflow-auto flex-1 border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700 text-xs uppercase sticky top-0">
                <tr>
                  <th className="py-2 px-4 text-left">Field</th>
                  <th className="py-2 px-4 text-left">Page</th>
                  <th className="py-2 px-4 text-center">Visible</th>
                  <th className="py-2 px-4 text-center">Mandatory</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4} className="py-8 text-center"><i className="fas fa-spinner fa-spin text-blue-400" /></td></tr>
                ) : filteredConfigs.length === 0 ? (
                  <tr><td colSpan={4} className="py-8 text-center text-gray-400">No configs</td></tr>
                ) : filteredConfigs.map((c, idx) => (
                  <tr key={c.id || idx} className={`border-t ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                    <td className="py-2 px-4 font-medium">{c.fieldName}</td>
                    <td className="py-2 px-4 text-xs text-gray-500">{c.pageName || "General"}</td>
                    <td className="py-2 px-4 text-center">
                      <button onClick={() => toggleField(c, "visible", !c.visible)} className={`w-10 h-5 rounded-full transition ${c.visible ? "bg-green-500" : "bg-gray-300"}`}>
                        <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${c.visible ? "translate-x-5" : "translate-x-0.5"}`} />
                      </button>
                    </td>
                    <td className="py-2 px-4 text-center">
                      <button onClick={() => toggleField(c, "mandatory", !c.mandatory)} className={`w-10 h-5 rounded-full transition ${c.mandatory ? "bg-green-500" : "bg-gray-300"}`}>
                        <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${c.mandatory ? "translate-x-5" : "translate-x-0.5"}`} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Dashboard Config Tab ─────────────────────────────────────────────────────
const DashboardTab = ({ showToast }) => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { stockService.adminGetClients().then(setClients).catch(() => {}); }, []);

  const loadWidgets = async (clientCode) => {
    setSelectedClient(clientCode);
    if (!clientCode) { setWidgets([]); return; }
    setLoading(true);
    try { setWidgets(await stockService.adminGetDashboardWidgets(clientCode)); }
    catch { setWidgets([]); showToast("error", "Failed to load widgets"); }
    finally { setLoading(false); }
  };

  const toggleWidget = async (widget) => {
    try {
      await stockService.adminSaveDashboardWidget({ clientCode: selectedClient, widgetKey: widget.widgetKey, visible: !widget.visible });
      setWidgets((prev) => prev.map((w) => w.widgetKey === widget.widgetKey ? { ...w, visible: !w.visible } : w));
    } catch { showToast("error", "Update failed"); }
  };

  const initWidgets = async () => {
    if (!selectedClient) return;
    try {
      const res = await stockService.adminInitDashboardWidgets(selectedClient);
      showToast("success", `Initialized ${res.created} widgets`);
      loadWidgets(selectedClient);
    } catch { showToast("error", "Init failed"); }
  };

  const updateOrder = async (widget, newOrder) => {
    try {
      await stockService.adminSaveDashboardWidget({ clientCode: selectedClient, widgetKey: widget.widgetKey, sortOrder: newOrder });
      setWidgets((prev) => prev.map((w) => w.widgetKey === widget.widgetKey ? { ...w, sortOrder: newOrder } : w).sort((a, b) => a.sortOrder - b.sortOrder));
    } catch { showToast("error", "Update failed"); }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-3">
      <div className="flex items-center gap-3 flex-shrink-0">
        <select value={selectedClient} onChange={(e) => loadWidgets(e.target.value)} className="px-3 py-1.5 border border-blue-300 rounded-lg text-sm">
          <option value="">Select Client</option>
          {clients.map((c) => <option key={c.clientId} value={c.clientCode}>{c.clientName} ({c.clientCode})</option>)}
        </select>
        {selectedClient && (
          <button onClick={initWidgets} className="px-3 py-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold">
            <i className="fas fa-magic mr-1" />Init Defaults
          </button>
        )}
      </div>

      {selectedClient && (
        <div className="overflow-auto flex-1 border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700 text-xs uppercase sticky top-0">
              <tr>
                <th className="py-2 px-4 text-left">Widget</th>
                <th className="py-2 px-4 text-left">Key</th>
                <th className="py-2 px-4 text-center">Order</th>
                <th className="py-2 px-4 text-center">Visible</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="py-8 text-center"><i className="fas fa-spinner fa-spin text-blue-400" /></td></tr>
              ) : widgets.length === 0 ? (
                <tr><td colSpan={4} className="py-8 text-center text-gray-400">No widgets configured. Click "Init Defaults" to add.</td></tr>
              ) : widgets.map((w, idx) => (
                <tr key={w.id || idx} className={`border-t ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                  <td className="py-2 px-4 font-medium">{w.widgetLabel || w.widgetKey}</td>
                  <td className="py-2 px-4 text-xs font-mono text-gray-500">{w.widgetKey}</td>
                  <td className="py-2 px-4 text-center">
                    <input type="number" value={w.sortOrder || 0} min="0" max="99"
                      onChange={(e) => updateOrder(w, parseInt(e.target.value) || 0)}
                      className="w-14 px-2 py-1 border border-gray-300 rounded text-xs text-center" />
                  </td>
                  <td className="py-2 px-4 text-center">
                    <button onClick={() => toggleWidget(w)} className={`w-10 h-5 rounded-full transition ${w.visible ? "bg-green-500" : "bg-gray-300"}`}>
                      <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${w.visible ? "translate-x-5" : "translate-x-0.5"}`} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Settings Tab ────────────────────────────────────────────────────────────
const PREDEFINED_SETTINGS = [
  { settingKey: "lowStockThreshold",       label: "Low Stock Alert Qty",         unit: "qty",  min: 1, max: 10000, icon: "fa-exclamation-triangle", color: "text-orange-500", type: "int" },
  { settingKey: "expiryAlertDays",         label: "Expiry Alert Days",           unit: "days", min: 1, max: 365,   icon: "fa-calendar-times",      color: "text-red-500",    type: "int" },
  { settingKey: "stockSearchDefaultLoad",  label: "Stock Search: Auto-load on open", unit: "",  icon: "fa-search",              color: "text-blue-500",   type: "bool" },
];

const SettingsTab = ({ showToast }) => {
  const [clients, setClients]   = useState([]);
  const [selected, setSelected] = useState("");
  const [settings, setSettings] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [editKey, setEditKey]   = useState("");
  const [editVal, setEditVal]   = useState("");

  useEffect(() => { stockService.adminGetClients().then(setClients).catch(() => {}); }, []);

  const loadSettings = async (clientCode) => {
    setSelected(clientCode);
    setEditKey(""); setEditVal("");
    if (!clientCode) { setSettings([]); return; }
    setLoading(true);
    try { setSettings(await stockService.adminGetDashboardSettings(clientCode)); }
    catch { setSettings([]); showToast("error", "Failed to load settings"); }
    finally { setLoading(false); }
  };

  const startEdit = (item) => {
    setEditKey(item.settingKey);
    setEditVal(item.settingValue ?? "");
  };

  const handleSave = async () => {
    if (!editKey || !selected) return;
    const meta = PREDEFINED_SETTINGS.find((p) => p.settingKey === editKey);
    if (meta?.type === "bool") {
      // bool settings are saved immediately via toggle, not via this flow
      return;
    }
    const val  = parseInt(editVal, 10);
    if (isNaN(val) || val < (meta?.min ?? 1) || val > (meta?.max ?? 99999)) {
      showToast("error", `Value must be between ${meta?.min} and ${meta?.max}`); return;
    }
    setSaving(true);
    try {
      await stockService.adminSaveDashboardSetting(selected, {
        settingKey: editKey, settingValue: String(val),
        label: meta?.label ?? editKey,
      });
      showToast("success", "Setting saved");
      setEditKey(""); setEditVal("");
      loadSettings(selected);
    } catch { showToast("error", "Save failed"); }
    finally { setSaving(false); }
  };

  const handleBoolToggle = async (item) => {
    const newVal = !(item.settingValue === "true");
    try {
      await stockService.adminSaveDashboardSetting(selected, {
        settingKey: item.settingKey,
        settingValue: String(newVal),
        label: item.label,
      });
      showToast("success", "Setting saved");
      loadSettings(selected);
    } catch { showToast("error", "Save failed"); }
  };

  const merged = PREDEFINED_SETTINGS.map((p) => ({
    ...p, ...( settings.find((s) => s.settingKey === p.settingKey) || {}),
    saved: !!settings.find((s) => s.settingKey === p.settingKey),
  }));

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-3">
      <div className="flex items-center gap-3 flex-shrink-0">
        <select value={selected} onChange={(e) => loadSettings(e.target.value)}
          className="px-3 py-1.5 border border-blue-300 rounded-lg text-sm">
          <option value="">Select Client</option>
          {clients.map((c) => <option key={c.clientId} value={c.clientCode}>{c.clientName} ({c.clientCode})</option>)}
        </select>
      </div>

      {selected && (
        <div className="overflow-auto flex-1 border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700 text-xs uppercase sticky top-0">
              <tr>
                <th className="py-2 px-4 text-left">Setting</th>
                <th className="py-2 px-4 text-center">Current Value</th>
                <th className="py-2 px-4 text-center">Default</th>
                <th className="py-2 px-4 text-center">Status</th>
                <th className="py-2 px-4 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="py-8 text-center"><i className="fas fa-spinner fa-spin text-blue-400" /></td></tr>
              ) : merged.map((item, idx) => (
                <tr key={item.settingKey} className={`border-t ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                  <td className="py-2 px-4">
                    <div className="flex items-center gap-2">
                      <i className={`fas ${item.icon} ${item.color} text-sm`} />
                      <div>
                        <p className="font-medium text-xs">{item.label}</p>
                        <p className="text-xs text-gray-400 font-mono">{item.settingKey}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-4 text-center">
                    {item.type === "bool" ? (
                      <button
                        onClick={() => handleBoolToggle(item)}
                        className={`w-10 h-5 rounded-full transition ${item.settingValue === "true" ? "bg-green-500" : "bg-gray-300"}`}
                      >
                        <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${item.settingValue === "true" ? "translate-x-5" : "translate-x-0.5"}`} />
                      </button>
                    ) : editKey === item.settingKey ? (
                      <div className="flex items-center justify-center gap-1">
                        <input type="number" value={editVal}
                          onChange={(e) => setEditVal(e.target.value)}
                          min={item.min} max={item.max}
                          className="w-20 px-2 py-1 border border-blue-300 rounded text-xs text-center" />
                        <span className="text-xs text-gray-400">{item.unit}</span>
                      </div>
                    ) : item.saved ? (
                      <span className="text-sm font-bold text-indigo-700 bg-indigo-50 px-3 py-0.5 rounded-full border border-indigo-200">
                        {item.settingValue} {item.unit}
                      </span>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  <td className="py-2 px-4 text-center">
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                      {item.settingKey === "lowStockThreshold" ? "10 qty" : "30 days"}
                    </span>
                  </td>
                  <td className="py-2 px-4 text-center">
                    {item.saved
                      ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700"><i className="fas fa-check-circle mr-1" />Configured</span>
                      : <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"><i className="fas fa-clock mr-1" />Default</span>}
                  </td>
                  <td className="py-2 px-4 text-center">
                    {item.type === "bool" ? (
                      <span className="text-xs text-gray-400">toggle above</span>
                    ) : editKey === item.settingKey ? (
                      <div className="flex justify-center gap-1">
                        <button onClick={handleSave} disabled={saving}
                          className="text-green-600 hover:bg-green-50 px-1.5 py-0.5 rounded disabled:opacity-50">
                          {saving ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}
                        </button>
                        <button onClick={() => { setEditKey(""); setEditVal(""); }}
                          className="text-gray-500 hover:bg-gray-100 px-1.5 py-0.5 rounded">
                          <i className="fas fa-times" />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(item)}
                        className="text-xs text-blue-600 hover:text-blue-800 font-semibold">
                        <i className="fas fa-edit mr-1" />{item.saved ? "Edit" : "Configure"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Main Admin Page ──────────────────────────────────────────────────────────
const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState("clients");
  const [toast, setToast] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);
  const showToast = (type, message) => setToast({ type, message });

  useEffect(() => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const roles = (payload.roles || []).map((r) => r.toLowerCase());
        setAuthorized(roles.includes("super-admin-jash"));
      }
    } catch { setAuthorized(false); }
    finally { setChecking(false); }
  }, []);

  if (checking) return null;
  if (!authorized) return (
    <div className="h-full flex items-center justify-center bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50">
      <div className="text-center">
        <i className="fas fa-lock text-red-400 text-5xl mb-4 block" />
        <p className="text-lg font-bold text-gray-700">Access Denied</p>
        <p className="text-sm text-gray-500 mt-1">Super Admin privileges required</p>
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4 overflow-hidden">
      <Header showSearch={false} showCart={false} />
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-4 flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 flex-shrink-0">
          <h2 className="text-base font-bold text-blue-800"><i className="fas fa-cog mr-2" />Admin Panel</h2>
          <div className="flex items-center gap-1 bg-white rounded-lg p-1 border border-blue-200 overflow-x-auto">
            {TABS.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${activeTab === tab.id ? "bg-indigo-500 text-white shadow" : "text-gray-600 hover:bg-gray-100"}`}>
                <i className={`fas ${tab.icon} text-[10px]`} />{tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 min-h-0 p-4 flex flex-col">
          {activeTab === "clients"     && <ClientsTab showToast={showToast} />}
          {activeTab === "users"       && <UsersTab showToast={showToast} />}
          {activeTab === "fieldConfig" && <FieldConfigTab showToast={showToast} />}
          {activeTab === "dashboard"   && <DashboardTab showToast={showToast} />}
          {activeTab === "menu"        && <MenuTab showToast={showToast} />}
          {activeTab === "settings"    && <SettingsTab showToast={showToast} />}
        </div>
      </section>
    </div>
  );
};

export default AdminPanel;
